<script module lang="ts">
  /** A development build under Vite (and Vitest); false anywhere else. */
  function isDev(): boolean {
    return (import.meta as { readonly env?: { readonly DEV?: boolean } }).env?.DEV === true;
  }
</script>

<script lang="ts">
  /**
   * The app frame around a menu of tabs: on a phone a top bar (the wordmark
   * on Home, the tab's name elsewhere, and the round profile button), the
   * tab's content, and the tab bar along the bottom; from 1024px a left
   * sidebar with the wordmark above the tabs and the profile button (with the
   * player's name) below, and the tab's name over its content. The same
   * places, the width used. TwinTen's `MainMenu` frame.
   *
   * ## Back (the owner's rule, 2026-10-02)
   *
   * The frame owns it, through a {@link TabHistory}: leaving Home arms one
   * history entry; Back on any other tab returns Home and never closes the
   * app; switching between tabs that are not Home re-targets that entry (no
   * stacking); Back on Home does what the platform does. The frame keeps the
   * history in step with `active`.
   *
   * - Without `tabHistory` the frame makes its own, reports Back as
   *   `onselect(home)`, and gives its entry back when it unmounts.
   * - With `tabHistory` (a game whose menu unmounts while a game screen is
   *   up holds it at app level, so the entry stays under the game; see
   *   `tabHistory.ts`), Back calls **that history's `onreturn(from)`**, not
   *   `onselect`: the game moves itself to Home there, and the frame follows
   *   `active`. The frame never disposes a history it was given.
   *
   * ## The game's name and mark (since 1.4.0)
   *
   * Every game on the shell looks alike, so the frame says which game this
   * is, everywhere in the menu. `game` (`GameBrand`: `name`, `mark`) draws
   * the game's MARK, a rounded tile filled with the accent holding the
   * game's glyph, and its name:
   *
   * - on a phone, on Home: the mark and the name at the display step (2rem,
   *   800), the page's `<h1>`;
   * - on a phone, on any other tab: the small mark (named after the game for
   *   assistive technology) at the left of the tab's own title;
   * - from 1024px: the mark and, under it, the name at the display step, at
   *   the top of the sidebar.
   *
   * `brand`, the 1.2.0 wordmark snippet, still works: alone, it is drawn as
   * before (the title step, no mark); with `game`, it draws the name beside
   * the mark (its case, colour, tracking and any two-tone split are the
   * game's; its size is the frame's, inherited, so nothing in it sets a font
   * size). Pass one of the two; `game` is what a game adopting 1.4.0 passes.
   *
   * ## Everything else
   *
   * - `active` and `onselect` are controlled: the frame draws the tab it is
   *   told and reports presses (a press on the tab already showing is not
   *   reported).
   * - `wide`: the sidebar layout. Undefined (the default) follows
   *   `(min-width: 1024px)`; a game that owns its breakpoint passes it.
   * - `avatar` + `onavatar`: the round profile button (`AvatarButton`).
   * - `banner`: a strip under the top bar (an update waiting), spaced by
   *   the frame.
   * - `offline` (since 1.8.0): the offline notice, a quiet strip in the
   *   banner's place (over it, when both show): the game's words on the
   *   card colour, a `role="status"` region that stands in the frame while
   *   the prop is passed and holds its words only while `shown`, so going
   *   offline is announced. Test id `ui-app-frame-offline` by default.
   * - `children`: the tab's content, in the frame's scrolling `<main>`.
   *
   * ## The tablet rail (since 1.6.0, opt-in)
   *
   * `rail`: off unless `true`, so a game that passes nothing draws as on
   * 1.5.0. With it, a window `(min-width: 600px) and (max-width: 1023.98px)`
   * that is not `wide` gets the rail instead of the bar: the tabs down the
   * left (`TabBar layout="rail"`, 5.5rem wide, icons over labels), the game's
   * mark at its top and the round profile button at its foot; beside it the
   * tab's heading (the game's name on Home, the tab's name elsewhere), the
   * banner and the tab's content, in a column up to 40rem wide, centred.
   * Card lists of the templates go two to a row there (Home's game cards);
   * a `FullPage` or a `Sheet` opened meanwhile sits beside the rail. The
   * page around the frame gives it the room: it lifts its app column's
   * `max-width` in that range (INTEGRATION.md, `AppFrame`); in development
   * a frame that draws the rail but does not start at the window's edge says
   * so once in the console. The Back rule is the bar's, unchanged.
   *
   * The range also holds a phone on its side (844 x 390, 932 x 430,
   * 667 x 375): deliberately, as a side rail suits a short wide screen better
   * than a bar along the bottom (a game screen hides the frame anyway). The
   * rail's spacing tightens there, so five tabs fit 390px.
   *
   * **Which layout is chosen in the browser.** By default (`firstFrame`
   * `bar`, as in 1.6.0 and 1.7.0) the server and the client's first render
   * draw the bar; once the frame is in the document it reads the window and
   * swaps to the rail or the sidebar (before a client-only app's first
   * paint).
   *
   * **`firstFrame="auto"`: a CSS-first first frame (since 1.8.0, opt-in).**
   * Until the frame has read
   * the window (on the server, and in the client's first render, which
   * hydration must find the same) a layout the props do not decide is
   * `auto`: every navigation and header that can be drawn is in the markup
   * (the sidebar's unless `wide` is passed, the rail's with `rail`, the
   * bar's), and the frame's own media queries show the one the window asks
   * for and take the others out with `display: none` (out of the tab order
   * and the accessibility tree: one landmark). Their inner test ids wait for
   * the layout, so no id is there twice. A server-rendered frame thus paints
   * the right layout in its first frame, script or none. Right after mount
   * (before a client-only app's first paint) the frame reads the window,
   * keeps the copy on screen and removes the others; from then on it follows
   * the window as in 1.6.0. Only the navigation and the header are drawn
   * per layout: the tab's content keeps its one `<main>`, so neither this
   * nor a later layout change (a device turned) remounts it. `wide: true`
   * (the sidebar) and `wide: false` without `rail` (the bar) decide alone,
   * and are drawn so from the first.
   *
   * `data-layout` on the root says which is drawn: `bar`, `rail` or
   * `sidebar` (since 1.6.0), or `auto` in the first frame (1.8.0).
   *
   * Test ids (`testids`): `root` (default `ui-app-frame`, which also carries
   * `data-tab` = the active tab), `tabs` (`ui-app-frame-tabs`), `avatar`
   * (`ui-app-frame-avatar`), `title` (`ui-app-frame-title`, the tab's name),
   * `mark` (`ui-app-frame-mark`, the game's mark wherever it is drawn).
   * Each tab's own test id is its `ShellTab.testid`.
   */
  import { onDestroy, onMount, type Snippet, untrack } from 'svelte';

  import AvatarButton from '../components/AvatarButton.svelte';
  import GameMark from '../components/GameMark.svelte';
  import TabBar from '../components/TabBar.svelte';
  import { TabHistory } from '../tabHistory.js';
  import { claimRailWarning, railWarningSaid } from './railWarning.js';
  import type { ShellTab } from '../types.js';
  import type { AppFrameAvatar, AppFrameOffline, GameBrand } from './menuTypes.js';

  interface Props {
    readonly tabs: readonly ShellTab[];
    /** The tab showing. */
    readonly active: string;
    /** A tab press, or Back on a tab that is not Home (`onselect(home)`). */
    readonly onselect: (id: string) => void;
    /** The root tab; the first tab by default. */
    readonly home?: string | undefined;
    /** The game's name and mark (since 1.4.0; see "The game's name and mark"). */
    readonly game?: GameBrand | undefined;
    /** The game's wordmark: the 1.2.0 way, still drawn; with `game`, the name's words. */
    readonly brand?: Snippet | undefined;
    readonly avatar: AppFrameAvatar;
    readonly onavatar: () => void;
    readonly banner?: Snippet | undefined;
    /** The offline notice (since 1.8.0), where the banner goes: pass it with `shown`. */
    readonly offline?: AppFrameOffline | undefined;
    readonly children: Snippet;
    /** The sidebar layout; follows `(min-width: 1024px)` when undefined. */
    readonly wide?: boolean | undefined;
    /**
     * The tablet rail (since 1.6.0): `true` draws it in `(min-width: 600px)
     * and (max-width: 1023.98px)` when not `wide`; off otherwise.
     */
    readonly rail?: boolean | undefined;
    /**
     * The first frame (since 1.8.0, opt-in): `bar` (the default, 1.7.0's: the
     * server and the client's first render draw the bar) or `auto` (CSS-first:
     * a server-rendered frame paints the right layout before any script).
     */
    readonly firstFrame?: 'bar' | 'auto' | undefined;
    /** The Back rule's history, when the game holds it (see "Back"). */
    readonly tabHistory?: TabHistory | undefined;
    /** The tab bar's landmark name; the shell's own word by default. */
    readonly navLabel?: string | undefined;
    readonly testids?:
      | {
          readonly root?: string;
          readonly tabs?: string;
          readonly avatar?: string;
          readonly title?: string;
          readonly mark?: string;
        }
      | undefined;
  }
  const {
    tabs,
    active,
    onselect,
    home,
    game,
    brand,
    avatar,
    onavatar,
    banner,
    offline,
    children,
    wide,
    rail,
    firstFrame = 'bar',
    tabHistory,
    navLabel,
    testids,
  }: Props = $props();

  const WIDE_QUERY = '(min-width: 1024px)';
  /** The rail's range, written out (no range syntax: Safari before 16.4 cannot read it). */
  const RAIL_QUERY = '(min-width: 600px) and (max-width: 1023.98px)';

  /** Calls `set` with `media`'s answer now and on every change; returns the stop. */
  function followMedia(media: string, set: (matches: boolean) => void): () => void {
    const query = window.matchMedia(media);
    set(query.matches);
    const onChange = (event: MediaQueryListEvent): void => set(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }

  // The window is read once the frame is in the browser (1.6.0); until then
  // (on the server, and in the client's first render, which must match it)
  // the layout is `auto` unless the props alone decide it (1.8.0, below).
  let wideByMedia = $state(false);
  $effect(() => {
    if (wide !== undefined || typeof window.matchMedia !== 'function') return undefined;
    return followMedia(WIDE_QUERY, (matches) => (wideByMedia = matches));
  });
  const isWide = $derived(wide ?? wideByMedia);

  let railByMedia = $state(false);
  $effect(() => {
    if (rail !== true || typeof window.matchMedia !== 'function') return undefined;
    return followMedia(RAIL_QUERY, (matches) => (railByMedia = matches));
  });
  /**
   * The first frame (1.8.0, opt-in with `firstFrame="auto"`; the default
   * `bar` is 1.7.0's: the bar until the window is read). With `auto`,
   * before the window is read, a layout the props do not decide is `auto`:
   * each navigation that can be drawn is in the
   * markup, and the frame's own media queries show exactly one of them
   * (`display: none` on the others, so they are out of the tab order and
   * the accessibility tree: one landmark). A server-rendered frame thus
   * paints the right layout in its first frame, before (or without) any
   * script; a client mounts the same tree (so hydration finds the server's)
   * and settles on one layout right after mount, before the first paint,
   * keeping the copy on screen and removing the others.
   */
  let measured = $state(false);
  // After the media effects above (declared first, so run first), in the browser only.
  onMount(() => {
    measured = true;
  });
  /** The props decide alone: `wide: true` is the sidebar; `wide: false` without the rail, the bar. */
  const decided = $derived(wide === true || (wide === false && rail !== true));
  /** Which navigation is drawn: the bar, the rail (1.6.0), the sidebar, or (1.8.0) the window's. */
  const layout = $derived<'bar' | 'rail' | 'sidebar' | 'auto'>(
    firstFrame === 'auto' && !measured && !decided
      ? 'auto'
      : isWide
        ? 'sidebar'
        : rail === true && railByMedia
          ? 'rail'
          : 'bar',
  );
  const auto = $derived(layout === 'auto');
  /** Which navigations are in the markup: the one drawn, or in `auto` each that can be. */
  const drawn = $derived({
    sidebar: layout === 'sidebar' || (auto && wide === undefined),
    rail: layout === 'rail' || (auto && rail === true),
    bar: layout === 'bar' || auto,
  });

  let root = $state<HTMLDivElement | null>(null);

  // A layout change (a window resized, a device turned) draws the navigation
  // anew, and the old one goes with the element that had focus: a keyboard
  // player on a tab would be left on the page's body (1.8.0). So, just before
  // the swap, note whether focus was in the navigation or on the profile
  // button, and just after it put focus on the active tab in the new
  // navigation, or on the new profile button. Focus anywhere else (the tab's
  // content, which never remounts) is left alone.
  let drawnLayout: typeof layout | undefined;
  let refocus: 'tab' | 'avatar' | undefined;

  function focusInNavigation(): 'tab' | 'avatar' | undefined {
    const focused = document.activeElement;
    if (root === null || !(focused instanceof HTMLElement) || !root.contains(focused)) {
      return undefined;
    }
    // The tab's content is never remounted: a profile button or a list of
    // tabs a game draws there keeps its focus.
    if (focused.closest('.ui-frame-body') !== null) return undefined;
    if (focused.closest('.ui-avatar-button') !== null) return 'avatar';
    return focused.closest('.ui-tabs') === null ? undefined : 'tab';
  }

  $effect.pre(() => {
    const next = layout;
    untrack(() => {
      // Leaving `auto` keeps the copy on screen (and any focus in it).
      if (drawnLayout !== undefined && drawnLayout !== 'auto' && drawnLayout !== next) {
        refocus = focusInNavigation();
      }
      drawnLayout = next;
    });
  });

  $effect(() => {
    void layout;
    const where = refocus;
    refocus = undefined;
    if (where === undefined || root === null) return;
    const target =
      where === 'avatar'
        ? root.querySelector<HTMLElement>('.ui-avatar-button')
        : (root.querySelector<HTMLElement>('.ui-tabs .ui-tab[aria-current="page"]') ??
          root.querySelector<HTMLElement>('.ui-tabs .ui-tab'));
    target?.focus();
  });

  // The rail assumes the frame starts at the window's left edge (a full page
  // and a sheet sit beside it from there). A game that forgot to lift its app
  // column in the rail's range is told once, in development. Measured a
  // moment after the rail is drawn, once the page's own CSS (a column lifted
  // by `:has([data-layout='rail'])`) has caught up with the new attribute.
  $effect(() => {
    const frame = root;
    if (layout !== 'rail' || frame === null || railWarningSaid() || !isDev()) return undefined;
    const timer = setTimeout(() => checkRailEdge(frame), 250);
    return () => clearTimeout(timer);
  });

  function checkRailEdge(frame: HTMLElement): void {
    if (railWarningSaid() || !frame.isConnected) return;
    const left = frame.getBoundingClientRect().left;
    const inset = Number.parseFloat(getComputedStyle(frame).paddingLeft) || 0;
    if (left <= inset + 1 || !claimRailWarning()) return;
    console.warn(
      `AppFrame: the rail is drawn but the frame starts ${String(Math.round(left))}px from the window's left edge. Lift the app column's max-width in (min-width: 600px) and (max-width: 1023.98px) while [data-layout='rail'] is drawn (INTEGRATION.md, AppFrame); a FullPage or a Sheet is placed as if the frame started at the edge.`,
    );
  }

  const homeId = $derived(home ?? tabs[0]?.id ?? active);
  const activeTab = $derived(tabs.find((tab) => tab.id === active));
  const onHome = $derived(active === homeId);

  // The Back rule: the game's history, or one of the frame's own. Both are
  // read once — a frame does not change its root or its history.
  const ownHistory = untrack(() =>
    tabHistory === undefined
      ? new TabHistory({ home: homeId, onreturn: () => onselect(homeId) })
      : undefined,
  );
  const backRule: TabHistory = untrack(() => tabHistory ?? (ownHistory as TabHistory));

  $effect.pre(() => {
    backRule.show(active);
  });

  onDestroy(() => ownHistory?.dispose());

  function select(id: string): void {
    if (id === active) return;
    onselect(id);
  }

  // In `auto` (1.8.0) a navigation, a heading and a profile button stand in
  // the markup once per layout that can be drawn, so their test ids are left
  // off until the layout is settled: never two elements with one id. The
  // root's id is there from the start.
  const ids = $derived({
    root: testids?.root ?? 'ui-app-frame',
    tabs: auto ? undefined : (testids?.tabs ?? 'ui-app-frame-tabs'),
    avatar: auto ? undefined : (testids?.avatar ?? 'ui-app-frame-avatar'),
    title: auto ? undefined : (testids?.title ?? 'ui-app-frame-title'),
    mark: auto ? undefined : (testids?.mark ?? 'ui-app-frame-mark'),
  });
  /** The tabs as each navigation draws them: without their test ids in `auto`. */
  const navTabs = $derived(auto ? tabs.map((tab) => ({ ...tab, testid: undefined })) : tabs);

  // A frame with neither has no heading on Home; say so once.
  let warned = false;
  $effect(() => {
    if (game !== undefined || brand !== undefined || warned) return;
    warned = true;
    console.warn('AppFrame: pass `game` (or the 1.2.0 `brand` wordmark); Home has no heading.');
  });
</script>

<!-- The mark: the accent's tile with the game's glyph (`GameMark`, 1.6.0).
     `named` gives it the game's name (the small mark beside a tab's title,
     the rail's); otherwise the name is written beside it and the tile is
     decoration. -->
{#snippet markTile(size: 'sm' | 'md' | 'lg', named: boolean)}
  {#if game !== undefined}
    <GameMark name={game.name} mark={game.mark} {size} decorative={!named} testid={ids.mark} />
  {/if}
{/snippet}

{#snippet nameWords()}
  {#if brand !== undefined}{@render brand()}{:else if game !== undefined}{game.name}{/if}
{/snippet}

{#snippet wordmark()}
  {#if game === undefined}
    <h1 class="ui-frame-brand">{@render nameWords()}</h1>
  {:else}
    <h1 class="ui-frame-brand ui-frame-brand--game">
      {@render markTile('md', false)}
      <span class="ui-frame-name">{@render nameWords()}</span>
    </h1>
  {/if}
{/snippet}

{#snippet sideBrand()}
  {#if game === undefined}
    <h1 class="ui-frame-brand">{@render nameWords()}</h1>
  {:else}
    <h1 class="ui-frame-brand ui-frame-brand--side">
      {@render markTile('lg', false)}
      <span class="ui-frame-name">{@render nameWords()}</span>
    </h1>
  {/if}
{/snippet}

<!-- The rail's top: the mark alone. On Home the heading beside it already
     says the game's name, so the tile is decoration there; on any other tab
     it is named after the game. -->
{#snippet railBrand()}
  {@render markTile('md', !onHome)}
{/snippet}

{#snippet profile(showName: boolean)}
  <AvatarButton
    initial={avatar.initial}
    src={avatar.src}
    label={avatar.label}
    name={avatar.name}
    haspopup={avatar.haspopup ?? 'dialog'}
    {showName}
    testid={ids.avatar}
    onclick={onavatar}
  />
{/snippet}

{#snippet sidebarFooter()}
  {@render profile(true)}
{/snippet}

{#snippet railFooter()}
  {@render profile(false)}
{/snippet}

{#snippet bannerStrip()}
  {#if offline !== undefined}
    <!-- The offline notice (1.8.0): the region stays while the prop is
         passed; only its words come and go, so the change is announced. -->
    <div
      class="ui-frame-offline"
      class:ui-frame-offline--shown={offline.shown}
      role="status"
      data-testid={offline.testid ?? 'ui-app-frame-offline'}
    >
      {#if offline.shown}
        <span class="ui-frame-offline-message">{offline.message}</span>
        {#if offline.detail !== undefined}
          <span class="ui-frame-offline-detail">{offline.detail}</span>
        {/if}
      {/if}
    </div>
  {/if}
  {#if banner !== undefined}
    <div class="ui-frame-banner">{@render banner()}</div>
  {/if}
{/snippet}

<!-- One tree for the three layouts: only the navigation and the header
     change with the layout. The tab's content (`<main>`) keeps its place, so
     turning a device across the bar, the rail and the sidebar never remounts
     it (its scroll, its focus, a sheet it holds open), and a page the server
     drew as the bar hydrates without being thrown away. -->
<!-- Each layout's navigation and header in a block of its own (1.8.0): in
     `auto` every one that can be drawn is in the markup and the frame's
     media queries show one (the `ui-frame-only--*` classes); once the
     layout is settled the others go and the one on screen stays, never
     remounted. -->
<div
  class="ui-frame"
  class:ui-frame--wide={layout === 'sidebar'}
  class:ui-frame--rail={layout === 'rail'}
  class:ui-frame--auto={auto}
  class:ui-frame--wide-auto={auto && drawn.sidebar}
  class:ui-frame--rail-auto={auto && drawn.rail}
  data-testid={ids.root}
  data-tab={active}
  data-layout={layout}
  bind:this={root}
>
  {#if drawn.sidebar}
    <aside class="ui-frame-side ui-frame-only--sidebar">
      <TabBar
        tabs={navTabs}
        {active}
        layout="sidebar"
        brand={sideBrand}
        footer={sidebarFooter}
        label={navLabel}
        testid={ids.tabs}
        fixedTestids={!auto}
        onselect={select}
      />
    </aside>
  {/if}
  {#if drawn.rail}
    <div class="ui-frame-rail ui-frame-only--rail">
      <TabBar
        tabs={navTabs}
        {active}
        layout="rail"
        brand={game === undefined ? undefined : railBrand}
        footer={railFooter}
        label={navLabel}
        testid={ids.tabs}
        fixedTestids={!auto}
        onselect={select}
      />
    </div>
  {/if}
  <div class="ui-frame-main">
    {#if drawn.sidebar}
      <header class="ui-frame-top ui-frame-top--wide ui-frame-only--sidebar">
        <h2 class="ui-frame-title" data-testid={ids.title}>{activeTab?.label ?? ''}</h2>
      </header>
    {/if}
    {#if drawn.rail}
      <header class="ui-frame-top ui-frame-top--rail ui-frame-only--rail">
        {#if onHome}
          <h1 class="ui-frame-brand" class:ui-frame-brand--game={game !== undefined}>
            {@render nameWords()}
          </h1>
        {:else}
          <h1 class="ui-frame-title" data-testid={ids.title}>{activeTab?.label ?? ''}</h1>
        {/if}
      </header>
    {/if}
    {#if drawn.bar}
      <header class="ui-frame-top ui-frame-only--bar">
        {#if onHome}
          {@render wordmark()}
        {:else if game !== undefined}
          <div class="ui-frame-heading">
            {@render markTile('sm', true)}
            <h1 class="ui-frame-title" data-testid={ids.title}>{activeTab?.label ?? ''}</h1>
          </div>
        {:else}
          <h1 class="ui-frame-title" data-testid={ids.title}>{activeTab?.label ?? ''}</h1>
        {/if}
        {@render profile(false)}
      </header>
    {/if}
    {@render bannerStrip()}
    <main class="ui-frame-body">{@render children()}</main>
    {#if drawn.bar}
      <!-- `display: contents`: the bar lays out as the column's own child. -->
      <div class="ui-frame-only ui-frame-only--bar">
        <TabBar
          tabs={navTabs}
          {active}
          layout="bar"
          label={navLabel}
          testid={ids.tabs}
          fixedTestids={!auto}
          onselect={select}
        />
      </div>
    {/if}
  </div>
</div>

<style>
  .ui-frame {
    box-sizing: border-box;
    height: 100svh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background: var(--ui-ground);
    color: var(--ui-ink);
    font-family: var(--ui-font);
    padding-top: env(safe-area-inset-top);
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);
  }

  .ui-frame-top {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--ui-space-3);
    min-height: 3.5rem;
    padding: 0.4rem 0.9rem;
  }

  /* A header mark that stays on screen, not a splash: the title step, set
     tight. The wordmark's case, colour and weight are the game's snippet's. */
  .ui-frame-brand,
  .ui-frame-title {
    margin: 0;
    font: var(--ui-text-title);
    line-height: 1.1;
    color: var(--ui-ink);
  }

  /* With the game's mark: the name a step up, at the display step, so the
     game is the first thing read on Home (1.4.0). */
  .ui-frame-brand--game,
  .ui-frame-brand--side {
    display: flex;
    min-width: 0;
    font: var(--ui-text-display);
    line-height: 1.1;
    letter-spacing: -0.01em;
  }

  .ui-frame-brand--game {
    align-items: center;
    gap: var(--ui-space-3);
  }

  /* The sidebar is narrow: the mark above the name, so a long name still
     fits at the display step. */
  .ui-frame-brand--side {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--ui-space-3);
  }

  .ui-frame-name {
    min-width: 0;
    overflow-wrap: break-word;
  }

  .ui-frame-heading {
    display: flex;
    align-items: center;
    gap: var(--ui-space-3);
    min-width: 0;
  }

  .ui-frame-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }

  .ui-frame-banner {
    flex: 0 0 auto;
    margin: 0 var(--ui-space-3) var(--ui-space-2);
    display: flex;
    flex-direction: column;
  }

  /* From 1024px: the sidebar left, the tab right. */
  .ui-frame--wide {
    flex-direction: row;
  }

  .ui-frame-side {
    flex: 0 0 15.5rem;
    min-height: 0;
  }

  /* The header, the banner, the tab and (on a phone) the bar: in every
     layout, so the tab never changes parents. */
  .ui-frame-main {
    flex: 1;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .ui-frame-top--wide {
    padding: 1.25rem var(--ui-gutter-wide) 0;
  }

  /*
   * The tablet rail (1.6.0), in (min-width: 600px) and (max-width:
   * 1023.98px) when the game asks for it: the rail left, the tab right, its
   * heading, banner and content in one column up to 40rem wide, centred.
   * The column is padding, not a box, so the whole width still scrolls.
   */
  .ui-frame--rail {
    flex-direction: row;
  }

  .ui-frame-rail {
    flex: 0 0 auto;
    min-height: 0;
  }

  .ui-frame--rail .ui-frame-top--rail {
    justify-content: flex-start;
    padding: var(--ui-space-4) max(var(--ui-gutter), calc((100% - 40rem) / 2 + var(--ui-gutter)))
      var(--ui-space-2);
  }

  /* The offline notice (1.8.0): ink and ink-2 on the card colour, both
     gated pairs, in the banner's margins. Silent and out of the flow while
     it says nothing. */
  .ui-frame-offline {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    margin: 0 var(--ui-space-3) var(--ui-space-2);
    padding: var(--ui-space-3) var(--ui-space-4);
    border: 1px solid var(--ui-line);
    border-radius: var(--ui-radius-md);
    background: var(--ui-card);
    color: var(--ui-ink);
  }

  .ui-frame-offline:not(.ui-frame-offline--shown) {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: 0;
    padding: 0;
    border: 0;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  .ui-frame-offline-message {
    font: var(--ui-text-label);
  }

  .ui-frame-offline-detail {
    font: var(--ui-text-caption);
    font-weight: 400;
    color: var(--ui-ink-2);
  }

  .ui-frame--rail .ui-frame-offline,
  .ui-frame--rail .ui-frame-banner {
    margin-inline: max(var(--ui-space-3), calc((100% - 40rem) / 2 + var(--ui-space-3)));
  }

  .ui-frame--rail .ui-frame-body {
    padding-inline: max(0px, calc((100% - 40rem) / 2));
  }

  /*
   * The first frame (1.8.0): `auto`, before the window is read. Every
   * navigation that can be drawn is in the markup; these show the one the
   * window asks for and take the others out with `display: none` (out of
   * the tab order and the accessibility tree). The ranges are the ones the
   * script reads: the sidebar from 1024px (`ui-frame--wide-auto`: `wide`
   * not passed), the rail in (min-width: 600px) and (max-width: 1023.98px)
   * (`ui-frame--rail-auto`: `rail` passed), the bar everywhere else.
   */
  .ui-frame-only {
    display: contents;
  }

  @media (max-width: 1023.98px) {
    .ui-frame--auto .ui-frame-only--sidebar {
      display: none;
    }
  }

  @media (max-width: 599.98px), (min-width: 1024px) {
    .ui-frame--auto .ui-frame-only--rail {
      display: none;
    }
  }

  @media (min-width: 600px) and (max-width: 1023.98px) {
    .ui-frame--rail-auto .ui-frame-only--bar {
      display: none;
    }

    .ui-frame--rail-auto {
      flex-direction: row;
    }

    .ui-frame--rail-auto .ui-frame-top--rail {
      justify-content: flex-start;
      padding: var(--ui-space-4) max(var(--ui-gutter), calc((100% - 40rem) / 2 + var(--ui-gutter)))
        var(--ui-space-2);
    }

    .ui-frame--rail-auto .ui-frame-offline,
    .ui-frame--rail-auto .ui-frame-banner {
      margin-inline: max(var(--ui-space-3), calc((100% - 40rem) / 2 + var(--ui-space-3)));
    }

    .ui-frame--rail-auto .ui-frame-body {
      padding-inline: max(0px, calc((100% - 40rem) / 2));
    }
  }

  @media (min-width: 1024px) {
    .ui-frame--wide-auto .ui-frame-only--bar {
      display: none;
    }

    .ui-frame--wide-auto {
      flex-direction: row;
    }
  }

  /*
   * What the frame's navigation takes from the window's edges (1.8.0), for
   * whatever floats over the frame (a `Snackbar`) to keep clear of: the tab
   * bar's height above the bottom safe area (`--ui-frame-bottom`), or the
   * rail's or the sidebar's width at the left (`--ui-frame-left`; a
   * sidebar's frame is centred in the window, so half its width from the
   * window's middle is the middle of the content beside it). Set on the
   * root, in CSS alone, while a frame draws them (in `auto`, by the same
   * ranges), so a notice anywhere in the document reads them and nothing
   * measures or queries the page. Not while a full page covers the bar or
   * the sidebar, or a game screen replaces the frame.
   */
  :global(:root:has(.ui-frame[data-layout='bar']):not(:has(.ui-page, .ui-game-frame))) {
    --ui-frame-bottom: calc(var(--ui-tabbar-h) + 1px);
  }

  :global(:root:has(.ui-frame--rail):not(:has(.ui-game-frame))) {
    --ui-frame-left: 5.5rem;
  }

  :global(:root:has(.ui-frame--wide):not(:has(.ui-page, .ui-game-frame))) {
    --ui-frame-left: 15.5rem;
  }

  @media (max-width: 599.98px) {
    :global(:root:has(.ui-frame--auto):not(:has(.ui-page, .ui-game-frame))) {
      --ui-frame-bottom: calc(var(--ui-tabbar-h) + 1px);
    }
  }

  @media (min-width: 600px) and (max-width: 1023.98px) {
    :global(
      :root:has(.ui-frame--auto:not(.ui-frame--rail-auto)):not(:has(.ui-page, .ui-game-frame))
    ) {
      --ui-frame-bottom: calc(var(--ui-tabbar-h) + 1px);
    }

    :global(:root:has(.ui-frame--rail-auto):not(:has(.ui-game-frame))) {
      --ui-frame-left: 5.5rem;
    }
  }

  @media (min-width: 1024px) {
    :global(
      :root:has(.ui-frame--auto:not(.ui-frame--wide-auto)):not(:has(.ui-page, .ui-game-frame))
    ) {
      --ui-frame-bottom: calc(var(--ui-tabbar-h) + 1px);
    }

    :global(:root:has(.ui-frame--wide-auto):not(:has(.ui-page, .ui-game-frame))) {
      --ui-frame-left: 15.5rem;
    }
  }
</style>
